// PowerSync backend connector.
//
// Implements the PowerSyncBackendConnector interface. The primary
// responsibility is fetchCredentials(), which calls the backend's
// POST /powersync/token endpoint over the existing HttpOnly session
// cookie (citycare_session). There is NO MSAL on the frontend — the
// session cookie is the only auth signal here (per ADR-012).
//
// Token lifecycle:
//   - The SDK caches credentials internally.
//   - It calls fetchCredentials() again when the token has 30 seconds
//     or less remaining, proactively in the background.
//   - If PowerSync returns 401, the SDK invalidates the cached token
//     and calls fetchCredentials() on the next connection attempt. It
//     does NOT auto-redirect — that is our responsibility here.
//   - If the backend returns 401 (session expired), we redirect to
//     /auth/login so the user re-authenticates via Entra.
//
// uploadData() — maps each queued CRUD op to its backend endpoint (see
// the branch table in dispatchCrudEntry for the authoritative mapping:
// capture_items/spaces/floors/capture_sessions/asset_types). An op that
// reaches the queue without a mapped branch throws so the queue is
// preserved and the gap is loud, not silent — never best-effort-complete
// an unknown op.
//
// Why throw on unknown ops instead of "best-effort completing":
// calling batch.complete() acknowledges the batch to the SDK, which
// removes those entries from the local upload queue. Completing without
// uploading silently drops user changes — for asset counts that's a
// contractual data-loss bug. Throwing keeps the entries in the queue
// and surfaces the gap in logs / Sentry.
//
// See docs/plans/research/powersync-frontend-api.md §3 for the full
// connector interface and token lifecycle details.

// Imports are split into single-line statements rather than a single
// multi-line block so that lint:architecture (Rule 4) recognises each
// as a real import line. Continuation lines of a multi-line import
// look like non-import string matches to the awk-based scanner and
// trip the "direct PowerSync import" warning even from inside hooks/.
import { UpdateType } from '@powersync/web'
import type { AbstractPowerSyncDatabase, CrudEntry } from '@powersync/web'
import type { PowerSyncWriteDb } from './powersync-db'
import type { PowerSyncBackendConnector, PowerSyncCredentials } from '@powersync/web'

import { navigateTo } from '../api'

import { dispatchAssetTypeInsert } from './connector-dispatch-asset-types'
import {
  createDispatchContext,
  type DispatchContext,
} from './connector-dispatch-context'
import { dispatchCaptureItemInsert } from './connector-dispatch-create-item'
import { dispatchCaptureItemPatch } from './connector-dispatch-item-patch'
import { dispatchFloorInsert } from './connector-dispatch-floor'
import { dispatchSessionCancel } from './connector-dispatch-session-cancel'
import { dispatchSessionPatch } from './connector-dispatch-session'
import { dispatchSessionInsert } from './connector-dispatch-session-insert'
import { dispatchSpaceInsert } from './connector-dispatch-space'
import { routeSpaceDelete } from './connector-dispatch-space-delete'
import { dispatchSpaceUpdate } from './connector-dispatch-space-update'
import { hydrateSessionRehomes } from './connector-session-rehome-registry'
import {
  areUploadsPausedForReauth,
  isUploadAuthError,
  recoverUploadAuth,
  resumeUploadsAfterReauth,
} from './connector-upload-auth'
import {
  beaconUploadJam,
  clearUploadJam,
  hasUploadJams,
  isPermanentUploadError,
  isolatePoisonEntry,
  reconcileUploadJams,
} from './connector-upload-isolation'

// The capture_items PATCH path (updateItem) — re-home / LWW conflict /
// not_found drop — moved to connector-dispatch-item-patch.ts to stay under
// the 500-line lint:max-lines cap. `_resetLwwAttempts` is re-exported so the
// many tests that import it `from '../connector'` keep working unchanged.
export { _resetLwwAttempts } from './connector-dispatch-item-patch'

/** Response shape from POST /powersync/token (apps/backend/src/powersync/mint-token.ts) */
interface MintTokenResponse {
  token: string
  /** Unix timestamp (seconds) when the token expires */
  exp: number
  powersync_url: string
}

export class CitycareConnector implements PowerSyncBackendConnector {
  /**
   * Fetches a fresh PowerSync JWT from the backend.
   *
   * Uses credentials:'include' so the HttpOnly citycare_session cookie
   * is forwarded. Returns null if the session has expired — the caller
   * (SDK) will retry on the next connection attempt after the user
   * re-authenticates.
   */
  async fetchCredentials(): Promise<PowerSyncCredentials | null> {
    const res = await fetch('/powersync/token', {
      method: 'POST',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    })

    if (res.status === 401) {
      // Session expired — redirect to login. The PowerSync sync stream
      // will pause until the user re-authenticates and the connector is
      // re-connected via db.connect(connector) after the next /me check.
      navigateTo('/auth/login')
      return null
    }

    if (!res.ok) {
      throw new Error(
        `PowerSync token fetch failed with status ${res.status}`,
      )
    }

    const { token, exp, powersync_url } = (await res.json()) as MintTokenResponse

    // A fresh PowerSync token over the session cookie proves the session is
    // valid again, so lift any upload pause an earlier 401 set (F2/#808). This
    // is the reliable resume signal after a re-auth + reconnect — a local flag
    // flip, no network call, so it does not affect the token-fetch contract.
    resumeUploadsAfterReauth()

    return {
      endpoint: powersync_url,
      token,
      // exp is Unix seconds; PowerSyncCredentials.expiresAt is a Date.
      // The SDK uses this for its 30-second pre-refresh window.
      expiresAt: new Date(exp * 1000),
    }
  }

  /**
   * Upload local write queue to the backend.
   *
   * Phase E-2 scope: only `capture_items` PATCH (updateItem) is mapped;
   * other mutation kinds still go through the REST adapter and never
   * reach this queue. Unknown ops throw so we never silently drop a
   * write — see the file header for why batch.complete()-without-upload
   * is a data-loss bug.
   *
   * Each entry is dispatched sequentially, then `batch.complete()` acks
   * the whole batch. PER-ENTRY ISOLATION guards the ack: if an entry throws
   * a PERMANENT error (an `ApiError` whose code can never succeed on retry —
   * `isPermanentUploadError`), it is isolated via `isolatePoisonEntry` (its
   * count preserved in the device-local quarantine) and SKIPPED, so one
   * un-uploadable entry can't wedge the entire device queue behind it. Any
   * other throw — a transient 5xx / network failure / DB-closed, OR the
   * unmapped-op guard below — rethrows, so the batch stays queued and the
   * SDK retries the lot on the next tick. That over-retries already-uploaded
   * entries, but the backend handlers are idempotent under last-write-wins
   * via the HP1 Postgres trigger, so the retry is safe. See
   * connector-upload-isolation.ts for why this exists (the production
   * capture-not-found upload wedge).
   *
   * LWW conflict handling lives in `dispatchCrudEntry` — see its doc
   * comment. The contract (per docs/buildspec/capture/10-api.md §4 and
   * 20-ui.md §8.1/§8.7): on a stale-version response, apply the
   * authoritative row to local state and reapply the user's intent
   * with the bumped syncVersion ONCE. A second consecutive conflict
   * for the same row is reconciled to the bare authoritative state —
   * the user's edit is dropped and an item-reconciled signal is left
   * for the UI (currently via the local UPDATE alone; explicit event
   * emission is a follow-up wired through the adapter).
   */
  async uploadData(database: AbstractPowerSyncDatabase): Promise<void> {
    // F2/#808: uploads are PAUSED after an expired-session 401 that could not be
    // silently refreshed. Early-return (queue preserved, un-acked) so we don't
    // re-dispatch doomed 401 uploads or re-probe /auth/refresh every pass. This
    // is NOT a tight CPU/battery loop: the SDK's _uploadAllCrud guards against a
    // non-draining uploadData — when the head ps_crud item is unchanged across
    // iterations it throws "previously encountered CRUD item" and applies its
    // retry delay (~5 s) (@powersync/common AbstractStreamingSyncImplementation),
    // so a paused early-return is throttled to the retry cadence, not spun. A
    // successful fetchCredentials (re-auth + reconnect) or a full /auth/login
    // reload lifts the pause — see connector-upload-auth.ts.
    if (areUploadsPausedForReauth()) return

    const batch = await database.getCrudBatch()
    if (!batch) return

    const context = createDispatchContext()

    // Refill the durable session re-home cache before dispatching, so a
    // complete/reopen queued against an orphan whose INSERT was re-homed in a
    // PRIOR run (before an app reload) still redirects onto the authoritative
    // session instead of 404-ing (Codex on PR 660). On a transient read failure
    // this THROWS (bounded) to re-enter the cycle rather than dispatch off a cold
    // cache — see hydrateSessionRehomes. It runs before any entry dispatches, so
    // a re-entry is a clean whole-batch retry, never a half-applied batch.
    await hydrateSessionRehomes(database)

    // Prune jam keys for entries no longer queued (drained out-of-band /
    // superseded while wedged), so a stale key can't silently suppress a future
    // genuine wedge on a recycled id. Keeps "in jammedEntries ⟺ currently queued".
    reconcileUploadJams(batch.crud)

    let indexInPage = 0
    for (const entry of batch.crud) {
      try {
        await dispatchCrudEntry(entry, database, context)
        // dispatched cleanly — no longer jammed. Skip the key-build + Set.delete
        // entirely in the overwhelmingly-common no-jam case (audit-1 #7): when
        // nothing is jammed there is nothing to clear, so the alloc is pure waste
        // on the hot count-upload path.
        if (hasUploadJams()) clearUploadJam(entry)
      } catch (err) {
        // F2/#808: a 401 unauthenticated is its OWN class — the session cookie
        // expired. Attempt a silent credential refresh; if the session is gone,
        // PAUSE uploads + surface re-auth, leaving the batch UN-acked so the
        // queue is preserved (NEVER quarantine-drop a count for an auth blip,
        // NEVER bare-rethrow into an infinite ~5 s jam). A 'retry' (session
        // still valid, or the refresh failed transiently) falls through to the
        // transient beacon+rethrow below so the SDK retries the whole batch.
        // See connector-upload-auth.ts.
        if (isUploadAuthError(err)) {
          if ((await recoverUploadAuth()) === 'paused') return
        }
        // A non-transient failure for THIS entry must not wedge the whole
        // device queue (batch.complete() below acks every entry at once and
        // runs only if we reach it). Isolate a permanent ApiError — preserve
        // any count it carries, then let the entry drain with the batch.
        // Permanence is TABLE-AWARE (see isPermanentUploadError): a
        // capture_sessions invalid_argument/permission_denied is permanent
        // (drain — no count, no keep-queued contract), but the same codes on
        // spaces/capture_items rethrow to honour their dispatcher contracts.
        // Everything else (transient infra/server errors, the unmapped-op
        // guard) rethrows so the SDK preserves the queue and retries.
        if (!isPermanentUploadError(err, entry.table)) {
          // THE JAM. This rethrow skips `batch.complete()` below, so the SDK
          // retries the WHOLE batch every ~5 s and every entry after this one
          // never uploads — the production wedge that lost weeks of captures
          // while the app showed "✓ Saved". Historically it produced no Sentry
          // signal and no in-app surface (the SDK only sets
          // `SyncStatus.dataFlow.uploadError` and logs at debug), which is why
          // the loss was invisible. Beacon it FIRST — no PII, edge-triggered,
          // ids/codes/positions only — then rethrow unchanged to preserve the
          // keep-queued contract. See connector-upload-isolation.ts
          // `beaconUploadJam`.
          beaconUploadJam(err, entry, indexInPage, batch.crud.length, batch.haveMore)
          // Exiting the loop HERE (fail-fast) is what bounds `jammedEntries` to
          // ≤1 add per pass: at most one entry can beacon-then-throw before the
          // pass ends, so the Set can grow by at most one per uploadData call and
          // a single page (≤100 entries) caps a pass's contribution. Do NOT
          // convert this to a collect-then-throw-at-end loop without re-capping
          // the beacon — that would let every wedged entry in the page beacon in
          // one pass and let the Set grow unbounded across retries. (audit-1 #6)
          throw err
        }
        await isolatePoisonEntry(entry, database, err)
        clearUploadJam(entry) // isolated/drained — no longer jammed
      }
      indexInPage++
    }

    await batch.complete()
  }
}

/**
 * Map a single CRUD entry to its backend endpoint and POST/PUT it.
 * Exposed for tests; the connector calls this in a loop.
 *
 * The capture_items PATCH path (the most common mutation — updating an
 * asset count) is delegated to `dispatchCaptureItemPatch` in
 * connector-dispatch-item-patch.ts. See that file for the full contract:
 * re-home of orphaned increments onto an authoritative row, the
 * reapply-once-then-reconcile LWW handling, and the not_found drop.
 */
export async function dispatchCrudEntry(
  entry: CrudEntry,
  db: PowerSyncWriteDb,
  context: DispatchContext = createDispatchContext(),
): Promise<void> {
  if (entry.table === 'capture_items' && entry.op === UpdateType.PUT) {
    return dispatchCaptureItemInsert(entry, db, context)
  }
  if (entry.table === 'capture_items' && entry.op === UpdateType.DELETE) {
    return // orphan-drop ack from dispatchCaptureItemInsert's 409 path; row never reached the server. Codex P1 on the createItem PR.
  }
  if (entry.table === 'capture_items' && entry.op === UpdateType.PATCH) {
    // Re-home orphaned increments / LWW conflict / not_found drop — full
    // contract in connector-dispatch-item-patch.ts. The re-home mapping is
    // module-level (connector-rehome-registry.ts), so no per-batch context.
    return dispatchCaptureItemPatch(entry, db)
  }

  if (entry.table === 'capture_sessions' && entry.op === UpdateType.PUT) {
    return dispatchSessionInsert(entry, db, context)
  }
  if (entry.table === 'capture_sessions' && entry.op === UpdateType.PATCH) {
    return dispatchSessionPatch(entry)
  }
  if (entry.table === 'capture_sessions' && entry.op === UpdateType.DELETE) {
    return dispatchSessionCancel(entry) // user-cancel + orphan-drop; 200/409 ack; plain-orphan 404 acks+uploaded; RE-HOMED-orphan 404 preserves the authoritative session + surfaces, acks WITHOUT uploaded (H2, issue 700)
  }

  if (entry.table === 'asset_types' && entry.op === UpdateType.PUT) {
    return dispatchAssetTypeInsert(entry, db, context)
  }
  if (entry.table === 'asset_types' && entry.op === UpdateType.DELETE) {
    // Local-only DELETE — same orphan-drop semantics as capture_items
    // (see comment on the capture_items DELETE branch above). The
    // asset_types row was never accepted by the server (the POST 409'd),
    // so there's nothing to delete remotely. Ack to drain the queue.
    return
  }

  if (entry.table === 'spaces' && entry.op === UpdateType.PUT) {
    return dispatchSpaceInsert(entry, db, context)
  }
  if (entry.table === 'spaces' && entry.op === UpdateType.PATCH) {
    // STANDING INVARIANT (FFR2): the ONLY local UPDATE ever issued against
    // the spaces table is `alias`, so a spaces-PATCH ⟺ an alias edit.
    // dispatchSpaceUpdate maps it to the dedicated alias endpoint with a
    // partial `{ alias }` body (accepted BY DESIGN — no queue wedge).
    // NEVER `UPDATE spaces SET <non-alias column>` locally: it would
    // mis-route here, silently drop the change, and clobber the alias.
    // (Spaces analogue of the capture_items "never PATCH a non-quantity
    // column" rule; full rationale in connector-dispatch-space-update.ts.)
    return dispatchSpaceUpdate(entry, db, context)
  }
  if (entry.table === 'spaces' && entry.op === UpdateType.DELETE) {
    // A spaces-DELETE is one of TWO things needing OPPOSITE handling, and they
    // produce a byte-identical CRUD entry (op=DELETE, table=spaces, id, NO
    // opData) — the pending_space_deletes tombstone is the discriminator
    // (#596 / ADR-015). USER-INTENT room delete
    // (capture-client-powersync-delete-space.ts wrote a tombstone before its
    // local DELETE) → propagate DELETE /capture/sessions/:id/spaces/:spaceId,
    // recovering the session id from the tombstone (the gone row + empty opData
    // can't supply it). RE-MINT orphan-drop (connector-dispatch-space.ts) or
    // any other local-only DELETE → no tombstone → local ack. routeSpaceDelete
    // owns the lookup + dispatch-or-ack (kept out of this file for the line
    // cap); dispatchSpaceDelete clears the tombstone + emits `uploaded` on
    // success/404 and THROWS on a genuine 4xx/5xx (entry + tombstone retained
    // for an idempotent retry; NEVER throws on 404). `context` carries the
    // in-batch session re-home map so a delete queued under a 409-re-homed
    // session targets the authoritative session, not the orphan id.
    return routeSpaceDelete(entry, db, context)
  }

  if (entry.table === 'floors' && entry.op === UpdateType.PUT) {
    return dispatchFloorInsert(entry, db, context)
  }
  if (entry.table === 'floors' && entry.op === UpdateType.DELETE) {
    return // orphan-drop ack; symmetry + queue-wedge defence vs unexpected SDK DELETEs (see connector-dispatch-floor.test.ts)
  }

  throw new Error(
    `PowerSync uploadData: unsupported CRUD op ${entry.op} on table ` +
      `${entry.table}. See the branch table above for mapped ops; either ` +
      `an unmapped mutation kind reached the PowerSync queue, or the ` +
      `rollout flag widened without this connector being updated.`,
  )
}

// dispatchSessionPatch lives in connector-dispatch-session.ts — see
// that file for the full contract (complete-vs-reopen discrimination
// via `trackPrevious`, pre-bump syncVersion forwarding, 409 swallow
// semantics).
// dispatchAssetTypeInsert lives in connector-dispatch-asset-types.ts.
// dispatchCaptureItemPatch + handleLwwConflict live in
// connector-dispatch-item-patch.ts.

/** Module-level singleton — one connector for the lifetime of the app. */
export const connector = new CitycareConnector()
